<template>
  <main class="subscription-page">
    <!-- Header Card -->
        <PageHeader icon="fas fa-box-open">
      <template #title>
        Pilih Paket Langganan
      </template>
      <template #subtitle>
        Sesuaikan paket dan durasi berlangganan dengan kebutuhan operasional klinik
      </template>
      <template #actions>
        <span class="hc-schedule-header-sub">
                <i class="fas fa-shield-alt text-success mr-1"></i> Pembayaran aman
              </span>
      </template>
    </PageHeader>

    <div class="subscription-notice" role="note">
      <span class="subscription-notice__icon"><i class="fas fa-info-circle"></i></span>
      <p>
        Jika membeli paket baru saat sisa langganan lebih dari satu bulan, masa aktif
        akan diakumulasi satu bulan dan dikalkulasikan dengan paket baru.
      </p>
    </div>

    <div class="subscription-layout">
      <section class="subscription-catalog">
        <div class="subscription-section-heading">
          <div>
            <span class="subscription-eyebrow">Katalog paket</span>
            <h2>Paket tersedia</h2>
            <p>Pilih paket yang paling sesuai untuk pertumbuhan klinik Anda.</p>
          </div>
          <span class="subscription-package-count">{{ method.length || 0 }} paket</span>
        </div>

        <div class="subscription-package-grid">
          <article
            v-for="(val, i) in method"
            :id="val.name"
            :key="val.id || i"
            class="method-card subscription-package-card"
            :class="{ 'method-card-selected': payment.id === val.id }"
            role="button"
            tabindex="0"
            :aria-label="`Pilih paket ${val.name}`"
            @click="setPaymentMethod(val.id)"
            @keydown.enter.prevent="setPaymentMethod(val.id)"
            @keydown.space.prevent="setPaymentMethod(val.id)"
          >
            <span v-if="payment.id === val.id" class="subscription-selected-mark">
              <i class="fas fa-check"></i>
            </span>
            <span class="subscription-package-visual">
              <img
                :src="packageImage(val)"
                :alt="`Ilustrasi ${val.name}`"
              />
            </span>
            <span class="subscription-package-content">
              <span class="subscription-package-name">{{ val.name }}</span>
              <span v-if="val.badge" class="subscription-eyebrow">{{ val.badge }}</span>
              <small v-if="val.description">{{ val.description }}</small>
              <span class="subscription-package-price">
                <strong>Rp {{ parseInt(val.price).toLocaleString("id-ID") }}</strong>
                <small>/bulan</small>
              </span>
              <span class="subscription-package-meta">
                <span v-if="val.verticals && val.verticals.length"><i class="fas fa-check-circle"></i> {{ verticalNames(val.verticals) }}</span>
                <span><i class="fas fa-headset"></i> Dukungan layanan</span>
              </span>
              <span class="subscription-package-actions">
                <button type="button" class="subscription-detail-button" @click.stop="showPackageDetails(val)">
                  <i class="fas fa-list-ul"></i>
                  Lihat benefit
                </button>
                <button type="button" class="subscription-select-button" @click.stop="setPaymentMethod(val.id)">
                  {{ payment.id === val.id ? "Dipilih" : "Pilih" }}
                  <i :class="payment.id === val.id ? 'fas fa-check' : 'fas fa-arrow-right'"></i>
                </button>
              </span>
            </span>
          </article>
        </div>
      </section>

      <aside class="subscription-summary">
        <div class="subscription-summary__header">
          <div>
            <span class="subscription-eyebrow">Ringkasan</span>
            <h2>Detail berlangganan</h2>
          </div>
          <span class="subscription-summary__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" role="img"><path d="M6 2.75h12a1.25 1.25 0 0 1 1.25 1.25v17.25l-2.4-1.45-2.42 1.45L12 19.8l-2.43 1.45-2.42-1.45-2.4 1.45V4A1.25 1.25 0 0 1 6 2.75Zm2.1 4.2a.85.85 0 1 0 0 1.7h7.8a.85.85 0 1 0 0-1.7H8.1Zm0 4.1a.85.85 0 1 0 0 1.7h7.8a.85.85 0 1 0 0-1.7H8.1Zm0 4.1a.85.85 0 1 0 0 1.7h4.7a.85.85 0 1 0 0-1.7H8.1Z"/></svg>
          </span>
        </div>

        <div class="subscription-duration">
          <span class="subscription-summary-label">Durasi langganan</span>
          <b-form-radio-group
            id="radio-group-2"
            v-model="payment.duration"
            class="subscription-duration-options"
            name="radio-sub-component"
          >
            <b-form-radio
              v-for="(duration, index) in monthOption"
              :key="duration.value"
              :value="duration.value"
              @change="durationSet('default', index)"
            >
              {{ duration.value }} Bulan
            </b-form-radio>
          </b-form-radio-group>
        </div>

        <div v-if="payment.discount > 0" class="subscription-benefit">
          <i class="fas fa-gift"></i>
          <span v-if="payment.duration === 12">Harga tahunan hemat setara <strong>2 bulan</strong></span>
          <span v-else>Hemat <strong>{{ payment.discount }}%</strong> untuk durasi pilihan ini</span>
        </div>

        <div class="subscription-selected-package">
          <span class="subscription-summary-label">Paket berlangganan</span>
          <div>
            <strong>{{ payment.name.toUpperCase() }}</strong>
            <span>Rp {{ parseInt(payment.price).toLocaleString("id-ID") }}</span>
          </div>
        </div>

        <div class="subscription-price-list">
          <div v-if="payment.discount != 0">
            <span>Diskon {{ payment.discount }}%</span>
            <strong>- Rp {{ parseInt(payment.discount_value).toLocaleString("id-ID") }}</strong>
          </div>
          <div>
            <span>PPN {{ taxRate }}%</span>
            <strong>Rp {{ parseInt(payment.tax_amount).toLocaleString("id-ID") }}</strong>
          </div>
          <div>
            <span>Biaya layanan</span>
            <strong class="subscription-free">Gratis</strong>
          </div>
        </div>

        <div class="subscription-total">
          <span>Total pembayaran</span>
          <strong>Rp {{ parseInt(payment.total_amount).toLocaleString("id-ID") }}</strong>
          <small>{{ payment.duration }} bulan berlangganan</small>
        </div>

        <div class="subscription-payment-method">
          <span>Metode pembayaran</span>
          <strong>{{ payment.payment_method_name }}</strong>
        </div>

        <button
          id="checkout"
          type="button"
          class="subscription-checkout-button"
          :disabled="payment.id === 0"
          @click="checkout"
        >
          <span>{{ payment.id === 0 ? "Pilih paket dahulu" : "Lanjut ke pembayaran" }}</span>
          <i class="fas fa-arrow-right"></i>
        </button>

        <p class="subscription-summary-note">
          <i class="fas fa-lock"></i>
          Informasi pembayaran Anda dilindungi.
        </p>
      </aside>
    </div>

    <b-modal
      v-model="detailsVisible"
      size="lg"
      centered
      hide-footer
      modal-class="subscription-benefit-modal"
      body-class="subscription-benefit-modal__body"
    >
      <template #modal-title>
        <div v-if="selectedDetail" class="subscription-modal-title">
          <span class="subscription-eyebrow">Detail paket</span>
          <strong>{{ selectedDetail.name }}</strong>
        </div>
      </template>

      <div v-if="selectedDetail" class="subscription-modal-content">
        <div class="subscription-modal-hero">
          <img :src="packageImage(selectedDetail)" :alt="`Ilustrasi ${selectedDetail.name}`" />
          <div>
            <span v-if="selectedDetail.badge" class="subscription-modal-badge">{{ selectedDetail.badge }}</span>
            <h3>{{ selectedDetail.description }}</h3>
            <p v-if="selectedDetail.ideal_for"><strong>Cocok untuk:</strong> {{ selectedDetail.ideal_for }}</p>
            <div class="subscription-modal-price"><strong>Rp {{ parseInt(selectedDetail.price).toLocaleString("id-ID") }}</strong><span>/bulan</span></div>
          </div>
        </div>

        <section v-if="hasPackageCapacity(selectedDetail)" class="subscription-modal-section">
          <div class="subscription-modal-section__heading"><span>Kapasitas paket</span><small>Batas pemakaian yang termasuk</small></div>
          <div class="subscription-capacity-grid">
            <article><i class="fas fa-users"></i><div><strong>{{ capacityValue(selectedDetail, "users") }}</strong><span>Maks. pengguna</span></div></article>
            <article><i class="fas fa-clinic-medical"></i><div><strong>{{ capacityValue(selectedDetail, "branches") }}</strong><span>Maks. cabang</span></div></article>
            <article><i class="fas fa-database"></i><div><strong>{{ capacityValue(selectedDetail, "storage_gb", " GB") }}</strong><span>Kapasitas storage</span></div></article>
          </div>
        </section>

        <section v-if="selectedDetail.benefits && selectedDetail.benefits.length" class="subscription-modal-section">
          <div class="subscription-modal-section__heading"><span>Benefit utama</span><small>Nilai yang langsung dirasakan klinik</small></div>
          <div class="subscription-benefit-grid">
            <article v-for="(benefit, index) in selectedDetail.benefits" :key="`${selectedDetail.id}-benefit-${index}`">
              <span class="subscription-benefit-number">{{ String(index + 1).padStart(2, "0") }}</span>
              <div><strong>{{ benefit.title }}</strong><p>{{ benefit.description }}</p></div>
            </article>
          </div>
        </section>

        <section v-if="packageFacilities(selectedDetail).length" class="subscription-modal-section">
          <div class="subscription-modal-section__heading"><span>Fasilitas yang didapat</span><small>Sudah termasuk dalam paket</small></div>
          <div class="subscription-facility-grid">
            <span v-for="(facility, index) in packageFacilities(selectedDetail)" :key="`${selectedDetail.id}-facility-${index}`"><i class="fas fa-check"></i>{{ facility }}</span>
          </div>
        </section>

        <section v-if="selectedDetail.features && selectedDetail.features.length" class="subscription-modal-section subscription-technical-features">
          <div class="subscription-modal-section__heading"><span>Modul & fitur</span><small>Kapabilitas aktif pada paket</small></div>
          <div><span v-for="feature in selectedDetail.features" :key="feature.code">{{ feature.name }}</span></div>
        </section>

        <div class="subscription-modal-footer">
          <span><i class="fas fa-headset"></i> Tim Medhop siap membantu proses aktivasi.</span>
          <button type="button" @click="selectFromDetails">Pilih {{ selectedDetail.name }} <i class="fas fa-arrow-right"></i></button>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import clinicModule from "@/core/modules/CrudModule.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
// import { setting, getSetting } from '@/core/services/jwt.service.js'

export default {
  data() {
    return {
      // Form
      form: {},
      method: [],
      selectedDetail: null,
      detailsVisible: false,
      // data
      payment: {
        id: 0,
        name: "-",
        price: 0,
        duration: 3,
        tax_amount: 0,
        total_amount: 0,
        payment_method_name: "-",
        payment_method_code: "",
        payment_method_category: "",
        discount: "",
        discount_value: "",
        bonus: "",
      },
      duration: 1,
      // Other
      dataLoaded: false,
      taxRate: 11,
      monthOption: [
        {
          text: "3 Bulan",
          value: 3,
        },
        {
          text: "6 Bulan",
          value: 6,
        },
        {
          text: "9 Bulan",
          value: 9,
        },
        {
          text: "12 Bulan",
          value: 12,
        },
        {
          text: "24 Bulan",
          value: 24,
        },
      ],
    };
  },

  methods: {
    packageImage(plan) {
      if (plan && /^https?:\/\//i.test(plan.image_url || "")) return plan.image_url;
      const allowed = [
        "general-clinic",
        "clinic-network",
        "dental-clinic",
        "maternal-child",
        "therapy-clinic",
        "aesthetic-clinic",
        "corporate-health",
      ];
      const planImages = {
        solo: "general-clinic",
        "clinic-core": "general-clinic",
        network: "clinic-network",
        "dental-growth": "dental-clinic",
        "kia-family": "maternal-child",
        "therapy-growth": "therapy-clinic",
        "aesthetic-growth": "aesthetic-clinic",
        "mcu-corporate": "corporate-health",
      };
      const mappedImage = plan && planImages[plan.plan_code] ? planImages[plan.plan_code] : null;
      const imageKey = mappedImage || (plan && allowed.includes(plan.image_key) ? plan.image_key : "general-clinic");
      return `/media/subscription/plans/${imageKey}.png`;
    },
    hasPackageCapacity(plan) {
      const limits = (plan && plan.limits) || {};
      return ["users", "branches", "storage_gb"].some((key) => limits[key] !== null && limits[key] !== undefined);
    },
    capacityValue(plan, key, suffix = "") {
      const value = plan && plan.limits ? plan.limits[key] : null;
      return value === null || value === undefined ? "Tanpa batas" : `${Number(value).toLocaleString("id-ID")}${suffix}`;
    },
    packageFacilities(plan) {
      const facilities = plan && Array.isArray(plan.facilities) ? plan.facilities : [];
      return facilities.filter((item) => !/(akun|pengguna|cabang|storage|penyimpanan)/i.test(item));
    },
    showPackageDetails(plan) {
      this.selectedDetail = plan;
      this.detailsVisible = true;
    },
    selectFromDetails() {
      if (!this.selectedDetail) return;
      this.setPaymentMethod(this.selectedDetail.id);
      this.detailsVisible = false;
    },
    verticalNames(verticals) {
      return (verticals || []).map((item) => item.name).join(", ");
    },
    async getMonthOption() {
      //   this.form = await module.get('settings/' + this.$route.params.id)
      this.monthOption = await clinicModule.get("subscription/durations");
      // this.monthOption = this.monthOption.items;
    },

    async get() {
      //   this.form = await module.get('settings/' + this.$route.params.id)
      const catalog = await clinicModule.get("subscription/catalog");
      this.method = catalog && Array.isArray(catalog.items)
        ? catalog.items.slice().sort((a, b) => Number(a.sort_order || 999) - Number(b.sort_order || 999))
        : [];
      this.taxRate = catalog && catalog.commercial ? Number(catalog.commercial.tax_rate || 0) : 11;
    },

    setPaymentMethod(id) {
      // set data
      let find = this.method.find((x) => x.id == id);

      this.payment.id = find.id;
      this.payment.name = find.name;
      this.payment.price = find.price;
      this.payment.annual_price = find.annual_price;
      this.recalculatePayment();

    },

    durationSet(type, index) {
      if (type == "minus") {
        if (this.payment.duration > 3) {
          this.payment.duration -= 1;
        }
      } else if (type == "plus") {
        this.payment.duration += 1;
      } else {
        let monthOption = this.monthOption[index];
        this.payment.duration;
        this.payment.discount = monthOption.discount;
        this.payment.bonus = monthOption.bonus_amount || 0;
        this.recalculatePayment();
      }
    },

    recalculatePayment() {
      const regularSubtotal = Number(this.payment.price || 0) * Number(this.payment.duration || 0);
      let subtotal = regularSubtotal;
      if (Number(this.payment.duration) === 12 && Number(this.payment.annual_price) > 0) {
        subtotal = Number(this.payment.annual_price);
        this.payment.discount = regularSubtotal > 0
          ? Math.round(((regularSubtotal - subtotal) / regularSubtotal) * 100)
          : 0;
      } else if (Number(this.payment.discount) > 0) {
        subtotal = regularSubtotal * (1 - Number(this.payment.discount) / 100);
      }
      this.payment.after_price = Math.round(subtotal);
      this.payment.discount_value = Math.max(0, Math.round(regularSubtotal - subtotal));
      this.payment.tax_amount = Math.round(subtotal * Number(this.taxRate || 0) / 100);
      this.payment.total_amount = this.payment.after_price + this.payment.tax_amount;
    },

    async checkout() {
      localStorage.setItem("paymentDetailData", JSON.stringify(this.payment));

      this.$router.push("/subscribe/checkout");
    },

    back() {
      this.$router.push("/billings/list");
    },
  },

  mounted() {
    // BreadCrumb
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Berlangganan", route: "" },
      { title: "Checkout" },
    ]);
    // Get Data
    this.get();
    this.getMonthOption();
  },
};
</script>

<style lang="scss">
.subscription-page {
  --subscribe-navy: #143263;
  --subscribe-blue: #14608f;
  --subscribe-teal: #1e958f;
  --subscribe-border: #e3eaf2;
  --subscribe-muted: #536174;
  max-width: 1380px;
  margin: 0 auto;
  padding: 0 10px 30px;
  color: #1a2433;
}

.subscription-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 14px;
  padding: 20px 22px;
  overflow: hidden;
  border: 1px solid var(--subscribe-border);
  border-radius: 18px;
  background: linear-gradient(112deg, rgba(20, 50, 99, 0.08), rgba(20, 96, 143, 0.055), rgba(30, 149, 143, 0.08));
  box-shadow: 0 10px 28px rgba(20, 50, 99, 0.055);
}

.subscription-eyebrow {
  display: block;
  margin-bottom: 4px;
  color: var(--subscribe-teal);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.subscription-hero h1 {
  margin: 0 0 5px;
  color: #1a2433 !important;
  font-size: 25px !important;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.subscription-hero p,
.subscription-section-heading p {
  margin: 0;
  color: var(--subscribe-muted);
  font-size: 12px;
}

.subscription-secure-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 8px 12px;
  color: var(--subscribe-navy);
  border: 1px solid rgba(30, 149, 143, 0.2);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.78);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.subscription-secure-badge i {
  color: var(--subscribe-teal);
}

.subscription-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 13px;
  color: #526174;
  border: 1px solid #dce7f1;
  border-radius: 12px;
  background: #f6f9fc;
}

.subscription-notice__icon {
  color: var(--subscribe-blue);
}

.subscription-notice p {
  margin: 0;
  font-size: 10px;
  line-height: 1.55;
}

.subscription-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 18px;
  align-items: start;
}

.subscription-catalog,
.subscription-summary {
  border: 1px solid var(--subscribe-border);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(20, 50, 99, 0.055);
}

.subscription-catalog {
  padding: 18px;
}

.subscription-section-heading,
.subscription-summary__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.subscription-section-heading {
  margin-bottom: 16px;
}

.subscription-section-heading h2,
.subscription-summary__header h2 {
  margin: 0 0 4px;
  color: #1a2433 !important;
  font-size: 17px !important;
  font-weight: 700;
}

.subscription-package-count {
  padding: 6px 10px;
  color: var(--subscribe-blue);
  border: 1px solid rgba(20, 96, 143, 0.14);
  border-radius: 999px;
  background: rgba(20, 96, 143, 0.07);
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.subscription-package-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.subscription-package-card.method-card {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 286px;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  color: #1a2433;
  text-align: left;
  border: 1px solid var(--subscribe-border) !important;
  border-radius: 15px !important;
  background: #fff;
  box-shadow: 0 5px 16px rgba(20, 50, 99, 0.045);
  transition: transform 170ms ease, border-color 170ms ease, box-shadow 170ms ease;
}

.subscription-package-card.method-card:hover {
  border-color: rgba(30, 149, 143, 0.48) !important;
  box-shadow: 0 12px 26px rgba(20, 50, 99, 0.09);
  cursor: pointer;
  transform: translateY(-2px);
}

.subscription-package-card.method-card-selected {
  border-color: var(--subscribe-teal) !important;
  background: linear-gradient(150deg, #fff 70%, rgba(30, 149, 143, 0.06));
  box-shadow: 0 13px 28px rgba(30, 149, 143, 0.13);
}

.subscription-selected-mark {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: grid;
  width: 25px;
  height: 25px;
  place-items: center;
  color: #fff;
  border-radius: 8px;
  background: var(--subscribe-teal);
  font-size: 10px;
  box-shadow: 0 5px 12px rgba(30, 149, 143, 0.2);
}

.subscription-package-visual {
  display: grid;
  width: 100%;
  min-width: 0;
  height: 128px;
  min-height: 128px;
  padding: 14px;
  place-items: center;
  border-bottom: 1px solid #e7edf4;
  background: linear-gradient(145deg, #f3f7fb, #edf8f7);
}

.subscription-package-visual img {
  display: block;
  width: 108px;
  height: 96px;
  object-fit: contain;
}

.subscription-package-content {
  display: flex;
  min-width: 0;
  min-height: 184px;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 13px 14px 12px;
}

.subscription-package-name {
  width: 100%;
  min-height: 32px;
  margin-bottom: 4px;
  color: var(--subscribe-navy);
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
  text-transform: uppercase;
  white-space: normal;
  word-break: break-word;
}

.subscription-package-price {
  display: flex;
  align-items: baseline;
  gap: 3px;
  margin-bottom: 8px;
  white-space: nowrap;
}

.subscription-package-price strong {
  color: #1a2433;
  font-size: 15px;
  font-weight: 700;
}

.subscription-package-price small {
  color: var(--subscribe-muted) !important;
  font-size: 9px;
}

.subscription-package-meta {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  margin-bottom: 10px;
  color: #687588;
  font-size: 10px;
}

.subscription-package-meta span {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  min-height: 24px;
  line-height: 1.35;
}

.subscription-package-meta i {
  display: inline-grid;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  margin: 0 !important;
  padding: 0 !important;
  place-items: center;
  color: var(--subscribe-teal);
  font-size: 16px;
  line-height: 1;
}

.subscription-package-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 9px;
  color: var(--subscribe-blue);
  border-top: 1px solid #edf1f5;
  font-size: 10px;
  font-weight: 800;
}

.subscription-package-action i {
  font-size: 9px;
}

.subscription-summary {
  position: sticky;
  top: 92px;
  padding: 17px;
}

.subscription-summary__header {
  margin-bottom: 16px;
  padding-bottom: 13px;
  border-bottom: 1px solid #edf1f5;
}

.subscription-summary__icon {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  color: #fff;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--subscribe-navy), var(--subscribe-blue), var(--subscribe-teal));
}

.subscription-summary-label {
  display: block;
  margin-bottom: 7px;
  color: #536174;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.subscription-duration-options {
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 6px;
  margin-bottom: 13px;
}

.subscription-duration-options .custom-control {
  min-height: 0;
  margin: 0;
  padding: 0;
}

.subscription-duration-options .custom-control-input {
  position: absolute;
}

.subscription-duration-options .custom-control-label {
  display: flex;
  min-height: 32px;
  padding: 6px 5px;
  align-items: center;
  justify-content: center;
  color: #617085;
  border: 1px solid var(--subscribe-border);
  border-radius: 9px;
  background: #f8fafc;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  cursor: pointer;
}

.subscription-duration-options .custom-control-label::before,
.subscription-duration-options .custom-control-label::after {
  display: none;
}

.subscription-duration-options .custom-control-input:checked ~ .custom-control-label {
  color: #fff;
  border-color: transparent;
  background: linear-gradient(135deg, var(--subscribe-navy), var(--subscribe-blue), var(--subscribe-teal));
  box-shadow: 0 5px 12px rgba(20, 96, 143, 0.15);
}

.subscription-benefit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 13px;
  padding: 9px 10px;
  color: #166e69;
  border: 1px solid rgba(30, 149, 143, 0.17);
  border-radius: 10px;
  background: rgba(30, 149, 143, 0.08);
  font-size: 9px;
  line-height: 1.5;
}

.subscription-benefit i {
  margin-top: 2px;
}

.subscription-selected-package {
  padding: 11px;
  border: 1px solid var(--subscribe-border);
  border-radius: 11px;
  background: #f8fafc;
}

.subscription-selected-package > div,
.subscription-price-list > div,
.subscription-payment-method {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.subscription-selected-package strong,
.subscription-selected-package span {
  font-size: 11px;
}

.subscription-selected-package strong {
  color: var(--subscribe-navy);
}

.subscription-price-list {
  display: grid;
  gap: 9px;
  margin: 14px 0;
  padding: 0 2px;
}

.subscription-price-list span,
.subscription-price-list strong,
.subscription-payment-method {
  color: #5d6a7d;
  font-size: 10px;
}

.subscription-price-list strong {
  color: #35445a;
}

.subscription-price-list .subscription-free {
  color: var(--subscribe-teal);
  text-transform: uppercase;
}

.subscription-total {
  margin-bottom: 12px;
  padding: 15px 2px;
  border-top: 1px solid #dfe7ef;
  border-bottom: 1px solid #dfe7ef;
  background: transparent;
}

.subscription-total span,
.subscription-total strong,
.subscription-total small {
  display: block;
}

.subscription-total span {
  margin-bottom: 4px;
  color: #536174 !important;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.subscription-total strong {
  color: var(--subscribe-navy) !important;
  font-size: 23px;
  font-weight: 800;
  line-height: 1.25;
}

.subscription-total small {
  margin-top: 3px;
  color: #687588 !important;
  font-size: 11px;
  line-height: 1.45;
}

.subscription-payment-method {
  margin-bottom: 13px;
  padding: 0 2px;
}

.subscription-payment-method strong {
  color: var(--subscribe-navy);
}

.subscription-checkout-button {
  display: flex;
  width: 100%;
  min-height: 42px;
  padding: 9px 14px;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--subscribe-navy), var(--subscribe-blue), var(--subscribe-teal));
  box-shadow: 0 8px 18px rgba(20, 96, 143, 0.2);
  font-size: 11px;
  font-weight: 800;
  transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
}

.subscription-checkout-button:not(:disabled):hover {
  box-shadow: 0 11px 22px rgba(20, 96, 143, 0.26);
  transform: translateY(-1px);
}

.subscription-checkout-button:disabled {
  color: #8a96a8;
  background: #e8edf3;
  box-shadow: none;
  cursor: not-allowed;
}

.subscription-summary-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 9px 0 0;
  color: #8a96a8;
  font-size: 9px;
}

.subscription-summary-note i {
  color: var(--subscribe-teal);
}

@media (max-width: 1199.98px) {
  .subscription-package-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 991.98px) {
  .subscription-layout {
    grid-template-columns: 1fr;
  }

  .subscription-summary {
    position: static;
  }
}

@media (max-width: 767.98px) {
  .subscription-page {
    padding-right: 0;
    padding-left: 0;
  }

  .subscription-hero {
    align-items: flex-start;
    flex-direction: column;
    padding: 17px;
  }

  .subscription-hero h1 {
    font-size: 21px !important;
  }

  .subscription-catalog,
  .subscription-summary {
    padding: 14px;
  }
}

@media (max-width: 575.98px) {
  .subscription-package-grid {
    grid-template-columns: 1fr;
  }

  .subscription-package-visual {
    width: 100%;
    min-width: 0;
  }
}

/* Readability and interaction standardization */
.subscription-page .subscription-eyebrow {
  font-size: 12px;
  line-height: 1.4;
}

.subscription-notice {
  padding: 13px 15px;
}

.subscription-notice__icon {
  flex: 0 0 auto;
  font-size: 16px;
}

.subscription-notice p,
.subscription-section-heading p {
  color: #526174;
  font-size: 14px;
  line-height: 1.6;
}

.subscription-section-heading h2,
.subscription-summary__header h2 {
  font-size: 20px !important;
}

.subscription-package-count,
.subscription-summary-label {
  font-size: 12px;
  line-height: 1.4;
}

.subscription-package-card.method-card {
  min-height: 400px;
}

.subscription-package-visual {
  height: 154px;
  min-height: 154px;
  padding: 0;
}

.subscription-package-visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.subscription-package-content {
  min-height: 244px;
  padding: 16px;
}

.subscription-package-name {
  min-height: 0;
  margin-bottom: 8px;
  font-size: 16px;
  line-height: 1.4;
}

.subscription-package-content > small {
  display: block;
  margin-bottom: 9px;
  color: #526174;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.subscription-package-price strong {
  font-size: 19px;
}

.subscription-package-price small {
  font-size: 12px;
}

.subscription-package-meta {
  gap: 7px;
  color: #526174;
  font-size: 13px;
}

.subscription-package-meta span {
  align-items: flex-start;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.subscription-package-meta i {
  color: var(--subscribe-blue);
}

.subscription-package-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e8eef4;
}

.subscription-package-actions button {
  display: inline-flex;
  min-height: 38px;
  padding: 8px 10px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  white-space: nowrap;
}

.subscription-detail-button {
  color: var(--subscribe-blue);
  border: 1px solid #dce8f1;
  background: #f5f9fc;
}

.subscription-detail-button:hover {
  border-color: rgba(20, 96, 143, 0.35);
  background: #edf6fb;
}

.subscription-select-button {
  color: #fff;
  border: 1px solid var(--subscribe-blue);
  background: var(--subscribe-blue);
}

.method-card-selected .subscription-select-button {
  border-color: var(--subscribe-teal);
  background: var(--subscribe-teal);
}

.subscription-summary__icon svg {
  width: 20px;
  height: 20px;
  fill: #fff;
}

.subscription-duration-options .custom-control-label {
  min-height: 39px;
  padding: 8px 7px;
  font-size: 11px;
  line-height: 1.25;
}

.subscription-benefit,
.subscription-selected-package strong,
.subscription-selected-package span,
.subscription-price-list span,
.subscription-price-list strong,
.subscription-payment-method,
.subscription-checkout-button {
  font-size: 13px;
  line-height: 1.5;
}

.subscription-selected-package {
  padding: 15px;
}

.subscription-summary-note {
  color: #687588;
  font-size: 12px;
  line-height: 1.45;
}

.subscription-checkout-button {
  min-height: 48px;
}

.subscription-payment-method strong,
.subscription-selected-package strong,
.subscription-price-list strong {
  flex: 0 0 auto;
  color: #263b55;
  text-align: right;
}

.subscription-selected-package > div,
.subscription-price-list > div,
.subscription-payment-method {
  align-items: flex-start;
}

.subscription-summary-note i {
  color: var(--subscribe-blue);
  font-size: 15px;
}

/* Package benefit modal */
.subscription-benefit-modal {
  --subscribe-blue: #14608f;
  --subscribe-teal: #1e958f;
}

.subscription-benefit-modal .modal-content {
  overflow: hidden;
  border: 0;
  border-radius: 20px;
  box-shadow: 0 28px 80px rgba(12, 42, 72, 0.24);
}

.subscription-benefit-modal .modal-header {
  padding: 18px 22px;
  align-items: center;
  border-bottom: 1px solid #e6edf3;
}

.subscription-benefit-modal .modal-header .close {
  display: grid;
  width: 36px;
  height: 36px;
  padding: 0;
  place-items: center;
  color: #31506c;
  border-radius: 10px;
  background: #edf4f8;
  opacity: 1;
}

.subscription-benefit-modal__body {
  padding: 0 !important;
}

.subscription-modal-title strong {
  display: block;
  color: #17263a;
  font-size: 20px;
}

.subscription-modal-hero {
  display: grid;
  grid-template-columns: 245px minmax(0, 1fr);
  gap: 24px;
  padding: 22px;
  align-items: center;
  background: linear-gradient(135deg, #f3f8fb, #edf9f7);
}

.subscription-modal-hero img {
  width: 245px;
  height: 180px;
  object-fit: cover;
  border-radius: 15px;
  box-shadow: 0 10px 25px rgba(20, 50, 99, 0.12);
}

.subscription-modal-badge {
  display: inline-flex;
  margin-bottom: 10px;
  padding: 5px 9px;
  color: #137b76;
  border-radius: 999px;
  background: rgba(30, 149, 143, 0.12);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.subscription-modal-hero h3 {
  margin: 0 0 9px;
  color: #183153;
  font-size: 17px;
  line-height: 1.45;
}

.subscription-modal-hero p {
  margin: 0 0 13px;
  color: #607084;
  font-size: 13px;
  line-height: 1.55;
}

.subscription-modal-price {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.subscription-modal-price strong {
  color: #17263a;
  font-size: 24px;
}

.subscription-modal-price span {
  color: #6f7d8f;
  font-size: 12px;
}

.subscription-modal-section {
  padding: 20px 22px 0;
}

.subscription-modal-section__heading {
  display: flex;
  margin-bottom: 12px;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.subscription-modal-section__heading span {
  color: #183153;
  font-size: 15px;
  font-weight: 800;
}

.subscription-modal-section__heading small {
  color: #8190a2;
  font-size: 11px;
}

.subscription-benefit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.subscription-capacity-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.subscription-capacity-grid article {
  display: flex;
  min-height: 76px;
  padding: 13px;
  align-items: center;
  gap: 11px;
  border: 1px solid rgba(20, 96, 143, 0.12);
  border-radius: 13px;
  background: linear-gradient(145deg, #f6fafe, #f1fbf9);
}

.subscription-capacity-grid i {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  color: #fff;
  border-radius: 11px;
  background: var(--subscribe-blue);
  font-size: 15px;
}

.subscription-capacity-grid strong,
.subscription-capacity-grid span {
  display: block;
}

.subscription-capacity-grid strong {
  color: #183153;
  font-size: 17px;
}

.subscription-capacity-grid span {
  margin-top: 2px;
  color: #718096;
  font-size: 11px;
}

.subscription-benefit-grid article {
  display: flex;
  gap: 11px;
  padding: 13px;
  border: 1px solid #e3ebf2;
  border-radius: 13px;
  background: #fff;
}

.subscription-benefit-number {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  place-items: center;
  color: #fff;
  border-radius: 9px;
  background: linear-gradient(135deg, var(--subscribe-blue), var(--subscribe-teal));
  font-size: 10px;
  font-weight: 800;
}

.subscription-benefit-grid strong {
  display: block;
  margin-bottom: 4px;
  color: #263d58;
  font-size: 13px;
}

.subscription-benefit-grid p {
  margin: 0;
  color: #6a788a;
  font-size: 12px;
  line-height: 1.5;
}

.subscription-facility-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.subscription-facility-grid span {
  display: flex;
  min-height: 38px;
  padding: 9px 11px;
  align-items: center;
  gap: 9px;
  color: #506176;
  border-radius: 10px;
  background: #f5f8fb;
  font-size: 12px;
}

.subscription-facility-grid i {
  display: grid;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  place-items: center;
  color: #fff;
  border-radius: 50%;
  background: var(--subscribe-teal);
  font-size: 9px;
}

.subscription-technical-features > div:last-child {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.subscription-technical-features > div:last-child span {
  padding: 6px 9px;
  color: #4c6076;
  border: 1px solid #dfe8ef;
  border-radius: 8px;
  font-size: 11px;
}

.subscription-modal-footer {
  display: flex;
  margin-top: 20px;
  padding: 16px 22px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  color: #627287;
  border-top: 1px solid #e7edf2;
  background: #fafcfd;
  font-size: 12px;
}

.subscription-modal-footer > span i {
  margin-right: 7px;
  color: var(--subscribe-teal);
}

.subscription-modal-footer button {
  display: inline-flex;
  min-height: 42px;
  padding: 10px 15px;
  align-items: center;
  gap: 10px;
  color: #fff;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--subscribe-blue), var(--subscribe-teal));
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .subscription-modal-hero {
    grid-template-columns: 1fr;
  }

  .subscription-modal-hero img {
    width: 100%;
  }

  .subscription-benefit-grid,
  .subscription-facility-grid,
  .subscription-capacity-grid {
    grid-template-columns: 1fr;
  }

  .subscription-modal-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .subscription-modal-footer button {
    justify-content: center;
  }
}
</style>
